import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { setSubscriptionQueryClient } from "@/lib/subscription-bus";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { NotificationToasts } from "@/components/notifications/NotificationToasts";
import { QRAcceptPrompts } from "@/components/qr-orders/QRAcceptPrompts";
import { RemotePrintAgent } from "@/components/printing/RemotePrintAgent";
import { SoundUnlockBanner } from "@/components/notifications/SoundUnlockBanner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { StaffGate } from "@/components/auth/StaffGate";
import { RequirePlan } from "@/components/common/Gated";
import { RequireSubscription } from "@/components/common/RequireSubscription";
import { RouteFallback } from "@/components/public/RouteFallback";
import { TPSConnectDialogHost } from "@/components/print/TPSConnectDialogHost";
import { OutletFeaturesSync } from "@/components/app/OutletFeaturesSync";
import { VersionWatcher } from "@/components/app/VersionWatcher";
import { getStaffToken } from "@/lib/api";

const Index = lazy(() => import("./pages/Index"));
const SelectContext = lazy(() => import("./pages/SelectContext"));
const BrandShell = lazy(() => import("./components/brand/BrandShell"));
const BrandDashboard = lazy(() => import("./pages/brand/BrandDashboard"));
const BrandReports = lazy(() => import("./pages/brand/BrandReports"));
const BrandMenus = lazy(() => import("./pages/brand/BrandMenus"));
const BrandMenuDetail = lazy(() => import("./pages/brand/BrandMenuDetail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Orders = lazy(() => import("./pages/Orders"));
const Reservations = lazy(() => import("./pages/Reservations"));
const Tables = lazy(() => import("./pages/Tables"));
const Menu = lazy(() => import("./pages/Menu"));
const QRMenu = lazy(() => import("./pages/qr-menu/QRMenuPage"));
const Kitchen = lazy(() => import("./pages/Kitchen"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Settings = lazy(() => import("./pages/Settings"));
const SettingsLayout = lazy(() => import("./components/settings/SettingsLayout"));
const SecuritySettings = lazy(() => import("./pages/settings/SecuritySettings"));
const AccountSettings = lazy(() => import("./pages/settings/AccountSettings"));
const PrintSettings = lazy(() => import("./pages/settings/PrintSettings"));
const OutletDetailsSettings = lazy(() => import("./pages/settings/OutletDetailsSettings"));
const ContactDetailsSettings = lazy(() => import("./pages/settings/ContactDetailsSettings"));
const OutletTimingsSettings = lazy(() => import("./pages/settings/OutletTimingsSettings"));
const PaymentSettings = lazy(() => import("./pages/settings/PaymentSettings"));
const POSTerminalsSettings = lazy(() => import("./pages/settings/POSTerminalsSettings"));
const LoyaltyProgramSettings = lazy(() => import("./pages/settings/LoyaltyProgramSettings"));
const LoyaltyAnalytics = lazy(() => import("./pages/settings/LoyaltyAnalytics"));
const LoyaltyBalanceLookup = lazy(() => import("./pages/LoyaltyBalanceLookup"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportsLayout = lazy(() => import("./components/reports/ReportsLayout"));
const DiscountSummaryReport = lazy(() => import("./pages/reports/DiscountSummaryReport"));
const DailySalesReportPage = lazy(() => import("./pages/reports/DailySalesReportPage"));
const ItemSalesReportPage = lazy(() => import("./pages/reports/ItemSalesReportPage"));
const PaymentMixReportPage = lazy(() => import("./pages/reports/PaymentMixReportPage"));
const VoidsAndCompsReportPage = lazy(() => import("./pages/reports/VoidsAndCompsReportPage"));
const ShiftReportPage = lazy(() => import("./pages/reports/ShiftReportPage"));
const TaxSummaryReportPage = lazy(() => import("./pages/reports/TaxSummaryReportPage"));
const StaffPerformanceReportPage = lazy(() => import("./pages/reports/StaffPerformanceReportPage"));
const CategorySalesReportPage = lazy(() => import("./pages/reports/CategorySalesReportPage"));
const RefundReportPage = lazy(() => import("./pages/reports/RefundReportPage"));
const KOTTimingReportPage = lazy(() => import("./pages/reports/KOTTimingReportPage"));
const SalesByHourReportPage = lazy(() => import("./pages/reports/SalesByHourReportPage"));
const OrderMixReportPage = lazy(() => import("./pages/reports/OrderMixReportPage"));
const ModifierSalesReportPage = lazy(() => import("./pages/reports/ModifierSalesReportPage"));
const CustomerLedgerReportPage = lazy(() => import("./pages/reports/CustomerLedgerReportPage"));
const InventoryOnHandReport = lazy(() => import("./pages/reports/InventoryOnHandReport"));
const InventoryMovementsReport = lazy(() => import("./pages/reports/InventoryMovementsReport"));
const InventoryWastageReport = lazy(() => import("./pages/reports/InventoryWastageReport"));
const InventoryFoodCostReport = lazy(() => import("./pages/reports/InventoryFoodCostReport"));
const InventoryAvtReport = lazy(() => import("./pages/reports/InventoryAvtReport"));
const Downloads = lazy(() => import("./pages/Downloads"));
const Staff = lazy(() => import("./pages/Staff"));
const History = lazy(() => import("./pages/History"));
const Outlets = lazy(() => import("./pages/Outlets"));
const FeedbackOrders = lazy(() => import("./pages/FeedbackOrders"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const LoyaltyAutomations = lazy(() => import("./pages/LoyaltyAutomations"));
const InvoiceSequenceSettings = lazy(() => import("./pages/settings/InvoiceSequenceSettings"));
const TaxConfigurationSettings = lazy(() => import("./pages/settings/TaxConfigurationSettings"));
const ServiceChargesSettings = lazy(() => import("./pages/settings/ServiceChargesSettings"));
const DiscountsSettings = lazy(() => import("./pages/settings/DiscountsSettings"));
const KitchenSettings = lazy(() => import("./pages/settings/KitchenSettings"));
const QSRTokenSettings = lazy(() => import("./pages/settings/QSRTokenSettings"));
const TimeMachineSettings = lazy(() => import("./pages/settings/TimeMachineSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MenuDetailPage = lazy(() => import("./pages/menu/MenuDetailPage"));
const QRPortal = lazy(() => import("./pages/qr-portal/QRPortal"));
const QueueStatus = lazy(() => import("./pages/public/QueueStatus"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const FeesAndRefunds = lazy(() => import("./pages/FeesAndRefunds"));
const CRMDashboard = lazy(() => import("./pages/crm/CRMDashboard"));
const CRMCustomerList = lazy(() => import("./pages/crm/CustomerList"));
const CRMCustomer360 = lazy(() => import("./pages/crm/Customer360Page"));
const CRMSegments = lazy(() => import("./pages/crm/SegmentExplorer"));
const CRMCampaigns = lazy(() => import("./pages/crm/CampaignDashboard"));
const CRMCampaignComposer = lazy(() => import("./pages/crm/CampaignComposer"));
const CRMInbox = lazy(() => import("./pages/crm/WhatsAppInbox"));
// TemplateManager is now rendered as an embedded tab inside CampaignDashboard;
// the /crm/templates path redirects to /crm/campaigns?tab=templates.
const CRMFeedback = lazy(() => import("./pages/crm/FeedbackDashboard"));
const CRMRules = lazy(() => import("./pages/crm/CRMRulesManager"));

const queryClient = new QueryClient();
// Register so the raw fetch client (api.ts) can push subscription-expired
// signals directly into the React Query cache when any endpoint returns
// 402 / 403 SUBSCRIPTION_EXPIRED. Not all backend endpoints are gated yet,
// so this listens on every response, not just the billing-subscription one.
setSubscriptionQueryClient(queryClient);

/** Staff sessions gate on feedback.view and CRM; public customers keep an unauthenticated form. */
function StaffOrPublicFeedback() {
  if (getStaffToken()) {
    return (
      <StaffGate path="/feedback/:orderId">
        <FeedbackPage />
      </StaffGate>
    );
  }
  return <FeedbackPage />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <NotificationProvider>
        <TooltipProvider>
          <Toaster />
          <SoundUnlockBanner />
          <BrowserRouter>
            {/* Inside the router: "Open table" on a toast and Review on a prompt navigate. */}
            <NotificationToasts />
            <QRAcceptPrompts />
            {/* This till prints the KOTs and bills other devices could not. */}
            <RemotePrintAgent />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route
                  path="/select-context"
                  element={
                    <ProtectedRoute requireStaffSession={false}>
                      <SelectContext />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/brand"
                  element={
                    <ProtectedRoute requireStaffSession={false} requireBrandSession>
                      <BrandShell />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<BrandDashboard />} />
                  <Route path="reports" element={<BrandReports />} />
                  <Route path="menus" element={<BrandMenus />} />
                  <Route path="menus/:menuId" element={<BrandMenuDetail />} />
                </Route>
                <Route path="/dashboard" element={<StaffGate path="/dashboard"><Dashboard /></StaffGate>} />
                <Route path="/orders" element={<StaffGate path="/orders"><Orders /></StaffGate>} />
                <Route path="/history" element={<StaffGate path="/history"><History /></StaffGate>} />
                <Route path="/reservations" element={<StaffGate path="/reservations"><Reservations /></StaffGate>} />
                <Route path="/tables" element={<StaffGate path="/tables"><Tables /></StaffGate>} />
                <Route path="/menu" element={<StaffGate path="/menu"><Menu /></StaffGate>} />
                <Route
                  path="/menu/menus/:variantId"
                  element={
                    <StaffGate path="/menu/menus/:variantId">
                      <MenuDetailPage />
                    </StaffGate>
                  }
                />
                <Route path="/qr-menu" element={<StaffGate path="/qr-menu"><QRMenu /></StaffGate>} />
                <Route path="/kitchen" element={<StaffGate path="/kitchen"><Kitchen /></StaffGate>} />
                <Route path="/inventory" element={<StaffGate path="/inventory"><Inventory /></StaffGate>} />
                {/* Settings — nested layout with persistent sidebar rail.
                    PermissionRoute on the layout only; nested Discounts / Loyalty
                    keep their own discounts.manage gate. /settings/account stays
                    inside and is gated by billing.manage in MyAccountTab. */}
                <Route
                  path="/settings"
                  element={
                    <StaffGate path="/settings">
                      <SettingsLayout />
                    </StaffGate>
                  }
                >
                  <Route index element={<Settings />} />
                  <Route path="outlet-details" element={<RequireSubscription><OutletDetailsSettings /></RequireSubscription>} />
                  <Route path="contact-details" element={<RequireSubscription><ContactDetailsSettings /></RequireSubscription>} />
                  <Route path="outlet-timings" element={<RequireSubscription><OutletTimingsSettings /></RequireSubscription>} />
                  <Route path="invoice-sequence" element={<RequireSubscription><InvoiceSequenceSettings /></RequireSubscription>} />
                  <Route path="qsr-tokens" element={<RequireSubscription><QSRTokenSettings /></RequireSubscription>} />
                  <Route path="time-machine" element={<RequireSubscription><TimeMachineSettings /></RequireSubscription>} />
                  <Route path="payment" element={<RequireSubscription><PaymentSettings /></RequireSubscription>} />
                  <Route path="pos-terminals" element={<RequireSubscription><POSTerminalsSettings /></RequireSubscription>} />
                  <Route path="tax-configuration" element={<RequireSubscription><TaxConfigurationSettings /></RequireSubscription>} />
                  <Route path="service-charges" element={<RequireSubscription><ServiceChargesSettings /></RequireSubscription>} />
                  <Route path="kitchen" element={<RequireSubscription><KitchenSettings /></RequireSubscription>} />
                  <Route path="print" element={<RequireSubscription><PrintSettings /></RequireSubscription>} />
                  <Route path="discounts" element={<StaffGate path="/settings/discounts" nested><RequireSubscription><DiscountsSettings /></RequireSubscription></StaffGate>} />
                  <Route path="loyalty" element={<StaffGate path="/settings/loyalty" nested><RequireSubscription><RequirePlan feature="loyalty"><LoyaltyProgramSettings /></RequirePlan></RequireSubscription></StaffGate>} />
                  <Route path="loyalty/analytics" element={<StaffGate path="/settings/loyalty/analytics" nested><RequireSubscription><RequirePlan feature="loyalty"><LoyaltyAnalytics /></RequirePlan></RequireSubscription></StaffGate>} />
                  <Route path="security" element={<RequireSubscription><SecuritySettings /></RequireSubscription>} />
                  <Route path="account" element={<AccountSettings />} />
                </Route>
                {/* Reports — nested layout with persistent sidebar rail.
                    PermissionRoute on the layout only; nested reports inherit. */}
                <Route
                  path="/reports"
                  element={
                    <StaffGate path="/reports">
                      <ReportsLayout />
                    </StaffGate>
                  }
                >
                  <Route index element={<Reports />} />
                  <Route path="discount-summary" element={<DiscountSummaryReport />} />
                  <Route path="daily-sales" element={<DailySalesReportPage />} />
                  <Route path="item-sales" element={<ItemSalesReportPage />} />
                  <Route path="payment-mix" element={<PaymentMixReportPage />} />
                  <Route path="voids-and-comps" element={<VoidsAndCompsReportPage />} />
                  <Route path="shift" element={<ShiftReportPage />} />
                  <Route path="tax-summary" element={<TaxSummaryReportPage />} />
                  <Route path="staff-performance" element={<StaffPerformanceReportPage />} />
                  <Route path="category-sales" element={<CategorySalesReportPage />} />
                  <Route path="refunds" element={<RefundReportPage />} />
                  <Route path="kot-timing" element={<KOTTimingReportPage />} />
                  <Route path="sales-by-hour" element={<SalesByHourReportPage />} />
                  <Route path="sales-by-type" element={<OrderMixReportPage />} />
                  <Route path="modifier-sales" element={<ModifierSalesReportPage />} />
                  <Route path="customer-ledger" element={<CustomerLedgerReportPage />} />
                  <Route path="inventory-on-hand" element={<InventoryOnHandReport />} />
                  <Route path="inventory-movements" element={<InventoryMovementsReport />} />
                  <Route path="inventory-wastage" element={<InventoryWastageReport />} />
                  <Route path="inventory-food-cost" element={<InventoryFoodCostReport />} />
                  <Route path="inventory-avt" element={<InventoryAvtReport />} />
                </Route>
                <Route path="/downloads" element={<StaffGate path="/downloads"><Downloads /></StaffGate>} />
                <Route path="/staff" element={<StaffGate path="/staff"><Staff /></StaffGate>} />
                <Route path="/outlets" element={<StaffGate path="/outlets"><Outlets /></StaffGate>} />
                <Route path="/loyalty-automations" element={<StaffGate path="/loyalty-automations"><LoyaltyAutomations /></StaffGate>} />
                <Route path="/feedback" element={<StaffGate path="/feedback"><FeedbackOrders /></StaffGate>} />
                <Route path="/feedback/:orderId" element={<StaffOrPublicFeedback />} />
                <Route path="/loyalty/:outletCode" element={<LoyaltyBalanceLookup />} />
                <Route path="/order/:tableToken" element={<QRPortal />} />
                {/* Public walk-in queue status page, shared via WhatsApp link. */}
                <Route path="/queue/:token" element={<QueueStatus />} />
                {/* Public password-reset page — link target from the reset email. */}
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* Public privacy policy page — linked from the login footer. */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                {/* Public terms & conditions page — linked from the login footer. */}
                <Route path="/terms" element={<TermsAndConditions />} />
                {/* Public fees, eligibility and refunds page (website: /legal/eligibility) — linked from the
                    login footer, the legal pages and Settings > My Account. */}
                <Route path="/fees-and-refunds" element={<FeesAndRefunds />} />
                <Route path="/crm" element={<StaffGate path="/crm"><CRMDashboard /></StaffGate>} />
                <Route path="/crm/customers" element={<StaffGate path="/crm/customers"><CRMCustomerList /></StaffGate>} />
                <Route path="/crm/customers/:customerId" element={<StaffGate path="/crm/customers/:customerId"><CRMCustomer360 /></StaffGate>} />
                <Route path="/crm/segments" element={<StaffGate path="/crm/segments"><CRMSegments /></StaffGate>} />
                <Route path="/crm/campaigns" element={<StaffGate path="/crm/campaigns"><CRMCampaigns /></StaffGate>} />
                <Route path="/crm/campaigns/new" element={<StaffGate path="/crm/campaigns/new"><CRMCampaignComposer /></StaffGate>} />
                <Route path="/crm/campaigns/:id" element={<StaffGate path="/crm/campaigns/:id"><CRMCampaignComposer /></StaffGate>} />
                <Route path="/crm/inbox" element={<StaffGate path="/crm/inbox"><CRMInbox /></StaffGate>} />
                {/* Templates now live as a tab inside /crm/campaigns. Redirect
                    preserves old bookmarks and direct links. */}
                <Route path="/crm/templates" element={<Navigate to="/crm/campaigns?tab=templates" replace />} />
                <Route path="/crm/feedback" element={<StaffGate path="/crm/feedback"><CRMFeedback /></StaffGate>} />
                <Route path="/crm/rules" element={<StaffGate path="/crm/rules"><CRMRules /></StaffGate>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <TPSConnectDialogHost />
            {/* Keeps the selected outlet's feature switches (CRM on/off) current. */}
            <OutletFeaturesSync />
            {/* Reloads a tab left open across a deploy (see lib/versionCheck). */}
            <VersionWatcher />
          </BrowserRouter>
        </TooltipProvider>
      </NotificationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
